/**
 * Keys Service - Fetches key metadata (PK, FK, UNIQUE) for tables
 */

import type { DatabaseEngineId } from '@/core/engines';
import { executeQueryV2 } from './queryExecutor';
import { createExplorerExecutionId } from './explorerQueryUtils';
import { getQueries } from './queries';

export interface KeyColumnInfo {
  name: string;
  referencedSchema?: string;
  referencedTable?: string;
  referencedColumn?: string;
}

export interface KeyInfo {
  name: string;
  keyType: 'PRIMARY KEY' | 'FOREIGN KEY' | 'UNIQUE';
  columns: KeyColumnInfo[];
}

const KEY_TYPES = new Set(['PRIMARY KEY', 'FOREIGN KEY', 'UNIQUE']);

function toStr(value: unknown): string {
  if (value === null || value === undefined) return '';
  return String(value);
}

function getRowValue(row: Record<string, unknown>, keys: string[]): unknown {
  for (const key of keys) {
    const value = row[key];
    if (value !== undefined) return value;
  }
  return undefined;
}

export function mapKeyInfoRows(rows: Record<string, unknown>[]): KeyInfo[] {
  // Filter only key-type constraints
  const keyRows = rows.filter((row) => {
    const type = toStr(getRowValue(row, ['constraint_type']));
    return KEY_TYPES.has(type);
  });

  // Group by constraint name, tracking ordinal for correct column ordering
  const grouped = new Map<
    string,
    { keyType: string; columns: { col: KeyColumnInfo; ordinal: number }[] }
  >();

  for (const row of keyRows) {
    const name = toStr(getRowValue(row, ['constraint_name']));
    const keyType = toStr(getRowValue(row, ['constraint_type']));
    const columnName = toStr(getRowValue(row, ['column_name']));
    const ordinal = Number(getRowValue(row, ['column_ordinal']) ?? 0);

    if (!name || !columnName) continue;

    if (!grouped.has(name)) {
      grouped.set(name, { keyType, columns: [] });
    }

    const entry = grouped.get(name)!;
    const col: KeyColumnInfo = { name: columnName };

    if (keyType === 'FOREIGN KEY') {
      const refSchema = toStr(getRowValue(row, ['referenced_schema']));
      const refTable = toStr(getRowValue(row, ['referenced_table']));
      const refColumn = toStr(getRowValue(row, ['referenced_column']));
      if (refSchema) col.referencedSchema = refSchema;
      if (refTable) col.referencedTable = refTable;
      if (refColumn) col.referencedColumn = refColumn;
    }

    // Avoid duplicate columns (MSSQL INFORMATION_SCHEMA can return dupes for FK)
    if (!entry.columns.some((c) => c.col.name === columnName)) {
      entry.columns.push({ col, ordinal });
    }
  }

  return Array.from(grouped.entries()).map(([name, { keyType, columns }]) => ({
    name,
    keyType: keyType as KeyInfo['keyType'],
    columns: columns
      .sort((a, b) => a.ordinal - b.ordinal)
      .map((c) => c.col),
  }));
}

/**
 * Format a key's display name in SSMS style
 */
export function formatKeyDisplayName(key: KeyInfo): string {
  const colNames = key.columns.map((c) => c.name).join(', ');

  if (key.keyType === 'FOREIGN KEY' && key.columns.length > 0) {
    const firstCol = key.columns[0];
    const refSchema = firstCol.referencedSchema || '';
    const refTable = firstCol.referencedTable || '';
    const refCols = key.columns
      .map((c) => c.referencedColumn || '')
      .filter(Boolean)
      .join(', ');
    const refTarget = refSchema ? `${refSchema}.${refTable}` : refTable;
    return `${key.name} (${colNames}) -> ${refTarget}(${refCols})`;
  }

  return `${key.name} (${colNames})`;
}

export async function getKeys(
  connectionId: string,
  database: string,
  schema: string,
  tableName: string,
  engine?: DatabaseEngineId | string
): Promise<{ keys: KeyInfo[]; error?: string }> {
  if (engine === 'sqlite') {
    return getKeysSqlite(connectionId, database, tableName);
  }

  const queries = getQueries(engine);

  try {
    const result = await executeQueryV2({
      connectionId,
      sql: queries.tableConstraints(schema, tableName),
      database,
      executionId: createExplorerExecutionId(connectionId),
    });

    if (!result.success) {
      const errorMsg = result.error?.message || 'Unknown error';
      console.error(`[KeysService] Error getting keys: ${errorMsg}`);
      return { keys: [], error: errorMsg };
    }

    const rows = result.resultSets?.[0]?.rows || [];
    const keys = mapKeyInfoRows(rows);

    return { keys };
  } catch (error) {
    console.error('[KeysService] Error in getKeys:', error);
    return {
      keys: [],
      error: error instanceof Error ? error.message : 'Unknown error getting keys',
    };
  }
}

// --- SQLite-specific key extraction (PRAGMAs) ---

function escapeSqlite(v: string): string {
  return v.replace(/'/g, "''");
}

export function mapSqlitePkColumns(
  rows: Record<string, unknown>[],
  tableName: string
): KeyInfo | null {
  const cols = rows
    .filter((r) => Number(r.pk) > 0)
    .sort((a, b) => Number(a.pk) - Number(b.pk))
    .map((r) => ({ name: String(r.name) }));
  if (cols.length === 0) return null;
  return { name: `PK_${tableName}`, keyType: 'PRIMARY KEY', columns: cols };
}

export function mapSqliteForeignKeys(rows: Record<string, unknown>[]): KeyInfo[] {
  const grouped = new Map<
    number,
    { table: string; cols: { seq: number; col: KeyColumnInfo }[] }
  >();
  for (const row of rows) {
    const id = Number(row.id);
    const refTable = String(row.table || '');
    if (!grouped.has(id))
      grouped.set(id, { table: refTable, cols: [] });
    grouped.get(id)!.cols.push({
      seq: Number(row.seq),
      col: {
        name: String(row.from || ''),
        referencedTable: refTable,
        referencedColumn: String(row.to || ''),
      },
    });
  }
  return Array.from(grouped.entries()).map(([id, { table, cols }]) => ({
    name: `fk_${table}_${id}`,
    keyType: 'FOREIGN KEY' as const,
    columns: cols.sort((a, b) => a.seq - b.seq).map((c) => c.col),
  }));
}

export function mapSqliteUniqueIndexesToKeys(
  indexRows: Record<string, unknown>[],
  indexInfoMap: Map<string, Record<string, unknown>[]>
): KeyInfo[] {
  return indexRows
    .filter((r) => String(r.origin) === 'u')
    .map((r) => {
      const name = String(r.name);
      const cols = (indexInfoMap.get(name) || [])
        .sort((a, b) => Number(a.seqno) - Number(b.seqno))
        .map((ir) => ({ name: String(ir.name) }))
        .filter((c) => c.name);
      return { name, keyType: 'UNIQUE' as const, columns: cols };
    });
}

async function getKeysSqlite(
  connectionId: string,
  database: string,
  tableName: string
): Promise<{ keys: KeyInfo[]; error?: string }> {
  const esc = escapeSqlite(tableName);
  const exec = (sql: string) =>
    executeQueryV2({
      connectionId,
      sql,
      database,
      executionId: createExplorerExecutionId(connectionId),
    });

  try {
    const [tiResult, fkResult, ilResult] = await Promise.all([
      exec(`PRAGMA table_info('${esc}')`),
      exec(`PRAGMA foreign_key_list('${esc}')`),
      exec(`PRAGMA index_list('${esc}')`),
    ]);

    const keys: KeyInfo[] = [];

    if (tiResult.success) {
      const pk = mapSqlitePkColumns(tiResult.resultSets?.[0]?.rows || [], tableName);
      if (pk) keys.push(pk);
    }
    if (fkResult.success) {
      keys.push(...mapSqliteForeignKeys(fkResult.resultSets?.[0]?.rows || []));
    }
    if (ilResult.success) {
      const allIndexRows = ilResult.resultSets?.[0]?.rows || [];
      const uniqueRows = allIndexRows.filter((r) => String(r.origin) === 'u');
      const infoMap = new Map<string, Record<string, unknown>[]>();
      await Promise.all(
        uniqueRows.map(async (r) => {
          const idxName = String(r.name);
          const res = await exec(`PRAGMA index_info('${escapeSqlite(idxName)}')`);
          if (res.success) infoMap.set(idxName, res.resultSets?.[0]?.rows || []);
        })
      );
      keys.push(...mapSqliteUniqueIndexesToKeys(allIndexRows, infoMap));
    }

    return { keys };
  } catch (error) {
    console.error('[KeysService] Error in getKeysSqlite:', error);
    return {
      keys: [],
      error: error instanceof Error ? error.message : 'Unknown error getting keys',
    };
  }
}
